import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import {
  login as reduxLogin,
  logout as reduxLogout,
} from "../reduxToolkit/reducers/layout/authSlice";
import config from "../environments/environment";
import { store } from "../reduxToolkit/store";
import { toast } from "react-toastify";
import { ApiResponse } from "../models/ApiResponse";
import { RequestOptions } from "../models/RequestOptions";
// NOTE: environment import removed (not used)

class HttpService {
  private baseURL: string | undefined;
  private isInitialized = false;
  private authToken: string | null = null;
  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  private async init() {
    if (!this.isInitialized) {
      const configuration = await config;
      this.baseURL = configuration.baseURL;
      this.isInitialized = true;
    }
  }

  private get axiosInstance() {
    const state = store.getState();
    const token = this.authToken || state.auth.token;

    // Don't force a default Content-Type so FormData requests can set proper multipart boundaries automatically.
    const headers: Record<string, string> = {
      Accept: "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const instance = axios.create({
      baseURL: this.baseURL,
      headers,
      withCredentials: true,
    });

    // Strip Authorization header for public (anonymous) endpoints to avoid JWT middleware 401 on stale/invalid tokens
    instance.interceptors.request.use((config) => {
      const publicPatterns = [
        /\/api\/account\/login/i,
        /\/api\/account\/register/i,
        /\/api\/account\/refresh/i,
        /\/api\/account\/forgot-password/i,
        /\/api\/account\/reset-password/i,
      ];
      const url = config.url || "";
      // When axios is given a full URL it includes protocol+host; we only need the path part for matching
      const path = (() => {
        try {
          if (url.startsWith("http")) return new URL(url).pathname;
        } catch {
          /* ignore */
        }
        return url;
      })();
      if (publicPatterns.some((r) => r.test(path))) {
        if (config.headers) {
          delete (config.headers as any)["Authorization"];
        }
      }
      return config;
    });

    // Attach response interceptor (simple 401 handling for protected endpoints)
    instance.interceptors.response.use(
      (res) => res,
      async (error) => {
        const original = error.config;
        if (!original || original._retry) return Promise.reject(error);

        // Public paths that should bypass auth handling
        const publicPatterns = [
          /^\/api\/account\//i,
          /^\/api\/property\/list/i,
        ];
        const path = original.url || "";
        const isPublic = publicPatterns.some((r) => r.test(path));
        if (isPublic) return Promise.reject(error);

        if (error.response && error.response.status === 401) {
          original._retry = true;
          try {
            const currentState = store.getState();
            const refreshToken = currentState.auth.refreshToken;
            if (!refreshToken) throw new Error("No refresh token available");
            const refreshResp = await axios.post(
              `${this.baseURL}/api/account/refresh`,
              { refreshToken },
              { withCredentials: true }
            );
            const data = refreshResp.data?.data;
            if (data?.Token) {
              store.dispatch(
                reduxLogin({
                  token: data.Token,
                  refreshToken: data.RefreshToken,
                })
              );
              original.headers["Authorization"] = `Bearer ${data.Token}`;
              return instance(original);
            }
            throw new Error("Invalid refresh response");
          } catch (e) {
            store.dispatch(reduxLogout());
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }

  // Recursively resolves .NET-style $id/$ref/$values references
  private normalizeDotNetReferences<T>(data: any): T {
    const refMap: Record<string, any> = {};

    const resolveRefs = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(resolveRefs);
      } else if (obj && typeof obj === "object") {
        if ("$ref" in obj) return refMap[obj["$ref"]];

        const newObj: any = {};
        if ("$id" in obj) {
          refMap[obj["$id"]] = newObj;
        }

        for (const key in obj) {
          if (key === "$id") continue;
          if (key === "$values" && Array.isArray(obj[key])) {
            return resolveRefs(obj[key]);
          }
          newObj[key] = resolveRefs(obj[key]);
        }
        return newObj;
      } else {
        return obj;
      }
    };

    return resolveRefs(data);
  }

  // Unified response handler
  private async handleResponse<T>(
    axiosCall: Promise<AxiosResponse<ApiResponse<T>>>,
    unwrap = true,
    normalize = false,
    showToast = true
  ): Promise<T | ApiResponse<T>> {
    try {
      const response = await axiosCall;

      if (normalize) {
        response.data.data = this.normalizeDotNetReferences<T>(
          response.data.data
        );
      }

      if (!response.data.success) {
        if (showToast)
          toast.error(response.data.message || "Something went wrong");
        throw new Error(response.data.message || "API call failed");
      }

      if (showToast && response.data.message) {
        toast.success(response.data.message);
      }

      return unwrap ? response.data.data : response.data;
    } catch (error: any) {
      // Always log detailed error information for debugging
      console.error('HTTP Request Error:', {
        url: error?.config?.url,
        method: error?.config?.method,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        message: error?.message
      });

      if (showToast) {
        // Show detailed error information including status code
        const statusCode = error?.response?.status;
        const statusText = error?.response?.statusText;
        const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong";
        
        let displayMessage = errorMessage;
        if (statusCode) {
          displayMessage = `${statusCode} ${statusText}: ${errorMessage}`;
        }
        
        toast.error(displayMessage);
      }
      throw error;
    }
  }

  async get<T>(
    url: string,
    config: AxiosRequestConfig | undefined,
    options: RequestOptions & { unwrap: true }
  ): Promise<T>;

  async get<T>(
    url: string,
    config?: AxiosRequestConfig,
    options?: RequestOptions & { unwrap?: false }
  ): Promise<ApiResponse<T>>;

  async get<T>(
    url: string,
    config?: AxiosRequestConfig,
    options?: RequestOptions
  ): Promise<T | ApiResponse<T>> {
    await this.init();

    return this.handleResponse<T>(
      this.axiosInstance.get<ApiResponse<T>>(url, config),
      options?.unwrap ?? false,
      options?.normalize ?? false,
      options?.toast !== false
    );
  }

  // POST
  async post<T>(
    url: string,
    data: any,
    config: AxiosRequestConfig | undefined,
    options: RequestOptions & { unwrap: true }
  ): Promise<T>;

  async post<T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig,
    options?: RequestOptions & { unwrap?: false }
  ): Promise<ApiResponse<T>>;

  async post<T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig,
    options?: RequestOptions
  ): Promise<T | ApiResponse<T>> {
    await this.init();

    return this.handleResponse<T>(
      this.axiosInstance.post<ApiResponse<T>>(url, data, config),
      options?.unwrap ?? true,
      options?.normalize ?? false,
      options?.toast ?? true
    );
  }

  // PUT
  async put<T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig,
    options?: RequestOptions & { unwrap: true }
  ): Promise<T>;

  async put<T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig,
    options?: RequestOptions & { unwrap?: false | undefined }
  ): Promise<ApiResponse<T>>;

  // --- Implementation ---
  async put<T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig,
    options?: RequestOptions
  ): Promise<T | ApiResponse<T>> {
    await this.init();
    const response = await this.axiosInstance.put<ApiResponse<T>>(
      url,
      data,
      config
    );

    if (options?.unwrap) {
      return response.data.data;
    }

    return response.data;
  }

  // DELETE
  async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
    options?: RequestOptions & { unwrap: true }
  ): Promise<T>;

  async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
    options?: RequestOptions & { unwrap?: false | undefined }
  ): Promise<ApiResponse<T>>;

  // --- Implementation ---
  async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
    options?: RequestOptions
  ): Promise<T | ApiResponse<T>> {
    const response = await this.axiosInstance.delete<ApiResponse<T>>(
      url,
      config
    );

    if (options?.unwrap) {
      return response.data.data;
    }

    return response.data;
  }
}

export const httpService = new HttpService();
